import { useState } from 'react'
import { format, isValid } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import StatCell from './StatCell'

/** Temporary: hide auto-renew Switch until re-enabled */
const SHOW_AUTO_RENEW_SWITCH = false

const formatDate = (value) => {
    if (!value) return '—'
    const d = new Date(value)
    if (!isValid(d)) return '—'
    return format(d, 'dd/MM/yyyy')
}

const NextPaymentStatBlock = ({
    subscription,
    onAutoRenewChange,
    autoRenewPending,
    showNextPeriod = false,
}) => {
    const [dialogIntent, setDialogIntent] = useState(null)

    const showControls = SHOW_AUTO_RENEW_SWITCH && onAutoRenewChange && subscription?.externalSubscriptionId
    const cancelAtPeriodEnd = subscription.cancelAtPeriodEnd
    const periodEndLabel = formatDate(subscription.currentPeriodEnd)

    const handleConfirm = async () => {
        if (!dialogIntent) return
        const nextCancelAtPeriodEnd = dialogIntent === 'turnOff'
        try {
            await onAutoRenewChange(nextCancelAtPeriodEnd)
            setDialogIntent(null)
        } catch {
            /* Apollo/onError surfaces toast */
        }
    }

    return (
        <>
            <StatCell
                className="tw:h-full"
                value={formatDate(showNextPeriod ? subscription.scheduledPeriodEnd : subscription.currentPeriodEnd)}
                label="Next payment"
                footer={cancelAtPeriodEnd ? 'Auto-renew off' : 'Auto-renew on'}
                footerTrailing={
                    showControls ? (
                        <Switch
                            checked={!cancelAtPeriodEnd}
                            disabled={autoRenewPending}
                            aria-label={cancelAtPeriodEnd ? 'Enable automatic renewal' : 'Disable automatic renewal'}
                            className="tw:data-[state=checked]:bg-blue-500 tw:data-[state=unchecked]:bg-[#263E57]"
                            onCheckedChange={(checked) => {
                                if (checked) {
                                    setDialogIntent('resume')
                                } else {
                                    setDialogIntent('turnOff')
                                }
                            }}
                        />
                    ) : undefined
                }
            />

            <Dialog open={dialogIntent !== null} onOpenChange={(open) => !open && setDialogIntent(null)}>
                <DialogContent className="tw:border-[#263E57] tw:bg-[#0F1F31]" showCloseButton={true}>
                    <DialogHeader wrapperClassName="tw:border-[#263E57]">
                        <DialogTitle className="tw:text-white">
                            {dialogIntent === 'turnOff' ? 'Turn off auto-renew?' : 'Resume auto-renew?'}
                        </DialogTitle>
                    </DialogHeader>
                    <DialogDescription className="tw:border-0 tw:px-6 tw:py-4 tw:text-[#97AFCD]">
                        {dialogIntent === 'turnOff' ? (
                            <>
                                Your subscription stays active until{' '}
                                <span className="tw:text-white">{periodEndLabel}</span>. After that date it will not
                                renew automatically unless you subscribe again.
                            </>
                        ) : (
                            <>
                                Your plan will renew automatically at the end of the current billing period (
                                <span className="tw:text-white">{periodEndLabel}</span>).
                            </>
                        )}
                    </DialogDescription>
                    <DialogFooter className="tw:border-t tw:border-[#263E57] tw:px-6 tw:py-4">
                        <Button type="button" variant="ghost" onClick={() => setDialogIntent(null)}>
                            Cancel
                        </Button>
                        <Button type="button" variant="secondary" disabled={autoRenewPending} onClick={handleConfirm}>
                            {autoRenewPending ? 'Updating…' : 'Confirm'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default NextPaymentStatBlock
